/**
 * Client-side media compression using browser Canvas API.
 * Reduces image file size before upload — no external dependencies.
 */

interface CompressOptions {
  /** Max width or height in px. Default: 1920 */
  maxDimension?: number;
  /** JPEG quality 0–1. Default: 0.8 */
  quality?: number;
  /** Skip compression if file is already under this size (bytes). Default: 200KB */
  skipUnder?: number;
}

const DEFAULTS: Required<CompressOptions> = {
  maxDimension: 1920,
  quality: 0.8,
  skipUnder: 200 * 1024, // 200 KB
};

/**
 * Load a File/Blob as an HTMLImageElement.
 */
function loadImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Failed to load image for compression"));
    };
    img.src = url;
  });
}

/**
 * Draw an image onto a canvas at the target dimensions and export as Blob.
 */
function canvasToBlob(
  img: HTMLImageElement,
  width: number,
  height: number,
  mimeType: string,
  quality: number,
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;

    const ctx = canvas.getContext("2d");
    if (!ctx) {
      reject(new Error("Canvas 2D context unavailable"));
      return;
    }

    // Use high-quality resampling
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";
    ctx.drawImage(img, 0, 0, width, height);

    canvas.toBlob(
      (blob) => {
        if (blob) resolve(blob);
        else reject(new Error("Canvas toBlob returned null"));
      },
      mimeType,
      quality,
    );
  });
}

/**
 * Compress an image File.
 *
 * - Skips files already under `skipUnder` bytes
 * - Skips non-image files (returns original)
 * - Resizes to fit within `maxDimension` (preserves aspect ratio)
 * - Converts to JPEG at `quality` (PNGs with transparency stay as PNG)
 * - If compressed result is larger than original, returns original
 */
export async function compressImage(
  file: File,
  opts?: CompressOptions,
): Promise<File> {
  const { maxDimension, quality, skipUnder } = { ...DEFAULTS, ...opts };

  // Only compress images
  if (!file.type.startsWith("image/")) return file;

  // Skip tiny files — compression overhead not worth it
  if (file.size <= skipUnder) return file;

  // Skip GIFs (animated) and SVGs (vector)
  if (file.type === "image/gif" || file.type === "image/svg+xml") return file;

  try {
    const img = await loadImage(file);
    let { naturalWidth: w, naturalHeight: h } = img;

    // Calculate target dimensions preserving aspect ratio
    if (w > maxDimension || h > maxDimension) {
      const ratio = Math.min(maxDimension / w, maxDimension / h);
      w = Math.round(w * ratio);
      h = Math.round(h * ratio);
    }

    // Choose output format:
    // - PNG stays PNG (preserves transparency) but with reduced dimensions
    // - Everything else → JPEG for better compression
    const isPng = file.type === "image/png";
    const outputMime = isPng ? "image/png" : "image/jpeg";
    const outputQuality = isPng ? undefined : quality;

    const blob = await canvasToBlob(img, w, h, outputMime, outputQuality ?? 0.8);

    // If compression made it bigger, return original
    if (blob.size >= file.size) return file;

    // Build a new File with the same name (adjust extension if format changed)
    const ext = isPng ? ".png" : ".jpg";
    const baseName = file.name.replace(/\.[^.]+$/, "");
    const newName = baseName + ext;

    return new File([blob], newName, {
      type: outputMime,
      lastModified: Date.now(),
    });
  } catch (err) {
    // On any compression error, fall back to original — never block the upload
    console.warn("Image compression failed, using original:", err);
    return file;
  }
}

/**
 * Generate a thumbnail Blob for preview (much smaller, for temp message display).
 */
export async function generateThumbnail(
  file: File,
  maxSize = 200,
): Promise<string | null> {
  if (!file.type.startsWith("image/")) return null;

  try {
    const img = await loadImage(file);
    let { naturalWidth: w, naturalHeight: h } = img;
    const ratio = Math.min(maxSize / w, maxSize / h);
    w = Math.round(w * ratio);
    h = Math.round(h * ratio);

    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    if (!ctx) return null;

    ctx.drawImage(img, 0, 0, w, h);
    return canvas.toDataURL("image/jpeg", 0.6);
  } catch {
    return null;
  }
}
